import SvgIcon from '@home/components/SvgIcon';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Progress } from 'tea-component';
import { getLocale } from 'umi';
import GptButton from './GptButton';
import {
  FailedTipsTextType,
  GPTErrorStatusType,
  GPTType,
  LoadingTextType,
  LoadingTipsType,
  PromptConfirmItemType,
} from '../types';
import { NO_DATE } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import './StatusTips.scss';
import { DEFAULT_MODAL } from '../constants';

const FailedTipsText: FailedTipsTextType = {
  cancelBtn: 'Get AI Summary',
  common: {
    main: {
      gpt: 'Due to the instability of the GPT API, GPT is currently unable to summarize the report as requested.',
      deepseek:
        'Due to the instability of the DeepSeek API, DeepSeek is currently unable to summarize the report as requested.',
      gemini:
        'Due to the instability of the Gemini API, Gemini is currently unable to summarize the report as requested.',
    },
    sub: 'Please try again later.',
  },
  little: {
    gpt: 'Due to insufficient selected content, GPT is currently unable to summarize the report as requested.',
    deepseek:
      'Due to the small number of selected reviews, DeepSeek is currently unable to summarize the report as requested.',
    gemini:
      'Due to the small number of selected reviews, Gemini is currently unable to summarize the report as requested.',
  },
  maxLimit: {
    main: 'Daily limit exceeded. Please try again tomorrow.',
  },
  min: {
    nodate: "Can't generate AI summary: No subtitle found",
    failed:
      'Unable to generate content summary due to source website restrictions or API instability, Please {btn}',
    urlerror: 'Unable to recognize the link',
    nodatasuccess:
      'Content scraped successfully, but no valid summary could be generated',
  },
  refreshBtn: 'Regenerate AI Summary',
};

function FailedTips({
  status,
  reload,
  isLittle = false,
  failedTipsText = {},
  errorType,
  isMini = false,
  promptInfo,
  showRefreshBtn = true,
}: {
  status: 'failed' | 'cancel';
  reload?: () => void;
  isLittle?: boolean;
  failedTipsText?: FailedTipsTextType;
  errorType?: GPTErrorStatusType;
  isMini?: boolean;
  promptInfo?: PromptConfirmItemType;
  showRefreshBtn?: boolean;
}) {
  const { formatMessage } = useLanguage();
  const {
    cancelBtn,
    commonTitle,
    commonSubTitle,
    littleTitle,
    maxLimit,
    refreshBtn,
    minText,
  } = useMemo(() => {
    const { cancelBtn, common, little, maxLimit, refreshBtn, min } = {
      ...FailedTipsText,
      ...(failedTipsText || {}),
    };

    const titleKey = promptInfo?.modelType || DEFAULT_MODAL;

    return {
      cancelBtn,
      commonTitle:
        typeof common?.main === 'string'
          ? common?.main
          : common?.main?.[titleKey],
      commonSubTitle:
        typeof common?.sub === 'string' ? common?.sub : common?.sub?.[titleKey],
      littleTitle: typeof little === 'string' ? little : little?.[titleKey],
      maxLimit,
      refreshBtn,
      minText: min,
    };
  }, [promptInfo]);
  const isEn = getLocale() === 'en-US';

  function commonTipsInfo() {
    return (
      <div>
        <p>
          {formatMessage({ id: commonTitle })}
          {!isEn &&
            commonSubTitle &&
            formatMessage({ id: commonSubTitle || NO_DATE.EMPTY })}
        </p>
        {isEn && commonSubTitle && (
          <p>{formatMessage({ id: commonSubTitle || NO_DATE.EMPTY })}</p>
        )}
      </div>
    );
  }

  function littleTipsInfo() {
    return (
      <div>
        <p>{formatMessage({ id: littleTitle })}</p>
      </div>
    );
  }

  function maxLimitTipsInfo() {
    return (
      <div>
        <p>{formatMessage({ id: maxLimit?.main })}</p>
      </div>
    );
  }

  function noDataTipsInfo() {
    return (
      <div>
        <p>{formatMessage({ id: NO_DATE.SELECTION })}</p>
      </div>
    );
  }

  function renderGptErrorText() {
    if (errorType === 'GPT_maxLimit') {
      return maxLimitTipsInfo();
    }

    if (errorType === 'GPT_nodata') {
      return noDataTipsInfo();
    }

    if (isLittle) {
      return littleTipsInfo();
    }

    return commonTipsInfo();
  }

  function renderMinTextInfo() {
    if (errorType === 'GPT_nodata')
      return formatMessage({ id: minText?.nodate });
    if (errorType === 'GPT_urlError') {
      return formatMessage({ id: minText?.urlerror });
    }
    if (errorType === 'GPT_nodataSuccess') {
      return formatMessage({ id: minText?.nodatasuccess });
    }
    return formatMessage(
      { id: minText?.failed },
      {
        btn: (
          <Button
            type="link"
            onClick={() => reload?.()}
            style={{ marginTop: '-2px' }}
          >
            {formatMessage({ id: 'Regenerate' })}
          </Button>
        ),
      },
    );
  }

  if (isMini) {
    return (
      <div>
        <div className="dbo-aigc-card-content-erry">
          <SvgIcon type="erry-icontip" className="dbo-aigc-card-content-icon" />
          <span>{renderMinTextInfo()}</span>
        </div>
      </div>
    );
  }

  if (status === 'cancel') {
    return (
      <GptButton
        text={cancelBtn}
        onClick={() => reload?.()}
        disabled={false}
      ></GptButton>
    );
  }

  const realShowRefreshBtn =
    !['GPT_maxLimit', 'GPT_nodata'].includes(errorType as string) &&
    showRefreshBtn;
  return (
    <div>
      <div className="dbo-aigc-card-content-erry">
        <SvgIcon type="erry-icontip" className="dbo-aigc-card-content-icon" />
        <span>{renderGptErrorText()}</span>
      </div>
      {realShowRefreshBtn && (
        <Button type="primary" onClick={() => reload?.()}>
          <SvgIcon type="refresh" />
          {formatMessage({ id: refreshBtn })}
        </Button>
      )}
    </div>
  );
}

const QueenTipsText: Array<string> = [
  'GPT will take 1 to 3 minutes, depending on the content.',
  'Please do not close this page.',
];

const defaultDeepseekList = [
  'DeepSeek will take 1 to 3 minutes, depending on the content.',
  'Please do not close this page.',
];

const defaultGeminiList = [
  'Gemini will take 1 to 3 minutes, depending on the content.',
  'Please do not close this page.',
];

const advancedList = [
  'Expected to wait for 0.5h~2h,  depending on the content.',
  'Please do not close this page.',
];

function Marquee({ list = QueenTipsText }: { list?: Array<any> }) {
  const [selected, setSelected] = useState(0);
  const { formatMessage } = useLanguage();

  useEffect(() => {
    if (!list?.length || list.length === 1) return;

    let timeout: any = null;

    function handleTimeout(curSelected: number) {
      const newSelected = curSelected === list.length - 1 ? 0 : selected + 1;
      setSelected(newSelected);
      timeout = setTimeout(() => handleTimeout(newSelected), 8000);
    }

    timeout = setTimeout(handleTimeout, 8000);
    return () => {
      timeout !== null && clearTimeout(timeout);
    };
  }, []);

  if (!list?.length) return null;

  if (list.length === 1) {
    return (
      <div className="dbo-commont-tip-text">
        {formatMessage({ id: list[selected] })}
      </div>
    );
  }

  return (
    <div className="dbo-commont-tip-text">
      {formatMessage({ id: list[selected] })}
    </div>
  );
}

const defaultLoadingTipsText: LoadingTextType = {
  title: {
    gpt: 'Generating a summary now using GPT-4',
    deepseek: 'Generating a summary now using DeepSeek',
    gemini: 'Generating a summary now using Gemini',
  },
  subTitle: undefined,
  totalTitle: '{total} Reviews are included',
  queen: {
    gpt: QueenTipsText,
    advanced: advancedList,
    deepseek: defaultDeepseekList,
    gemini: defaultGeminiList,
  },
  stopText: 'Stop',
};

/**
 * GPT loading 组件
 * @param status      状态
 * @param total       总数量
 * @param percent     进度
 * @param hasStopBtn  是否有stop按钮
 * @param handleStop  stop的操作
 * @param isMultiple  是否为多GPT模式
 * @param promptInfo  gpt的基础数据
 * @param loadingElement loading进度条与按钮之间的元素
 * @param topPercent  是否使用头部进度
 * @param loadingTipsText loading的文案展示
 * @param isMin min版本没有进度条没有进度文案 出现loading icon
 * @returns
 */
function LoadingTips({
  status,
  total,
  percent = 0,
  hasStopBtn = false,
  handleStop,
  isMultiple = false,
  promptInfo,
  loadingElement,
  loadingTipsText = {},
  topPercent = false,
  isMini = false,
  hasTotalText = true,
}: LoadingTipsType & {
  loadingTipsText?: LoadingTextType;
  topPercent?: boolean;
  hasTotalText?: boolean;
}) {
  const { formatMessage } = useLanguage();
  const themeMap = {
    loading: 'default',
    failed: 'danger',
    success: 'success',
    cancel: 'danger',
  };

  const {
    title,
    subTitle,
    totalTitle,
    queen: marqueList,
    stopText,
  } = useMemo(() => {
    const { title, subTitle, totalTitle, queen, stopText } = {
      ...defaultLoadingTipsText,
      ...(loadingTipsText || {}),
    };
    let queenKey: GPTType | 'advanced' | undefined = promptInfo?.modelType;

    if (promptInfo?.gptOptions === 'advanced') queenKey = 'advanced';

    const titleKey = promptInfo?.modelType || DEFAULT_MODAL;

    return {
      title: typeof title === 'string' ? title : title?.[titleKey],
      subTitle,
      totalTitle: hasTotalText ? totalTitle : undefined,
      queen: queen?.[queenKey || DEFAULT_MODAL] || [],
      stopText,
    };
  }, [promptInfo]);

  const { textEle, stopBtn } = useMemo(() => {
    const textEle = (
      <p>
        {formatMessage({ id: title || NO_DATE.EMPTY })}{' '}
        {!total || !totalTitle ? null : (
          <>({formatMessage({ id: totalTitle }, { total })})</>
        )}
        {subTitle && (
          <span style={{ color: '#838697' }}>
            {formatMessage({ id: subTitle })}
          </span>
        )}
      </p>
    );

    const stopBtn = hasStopBtn ? (
      <div className="dbo-aigc-btn-stop">
        <Button type="weak" onClick={() => handleStop?.()}>
          {formatMessage({ id: stopText })}
        </Button>
      </div>
    ) : null;

    return { textEle, stopBtn };
  }, [hasStopBtn, title, total, totalTitle, subTitle, formatMessage]);

  const { queenBox, classNames } = useMemo(() => {
    if (isMultiple) {
      return { queenBox: null, classNames: '--row' };
    }

    return {
      queenBox: (
        <div className="dbo-aigc-card-marquee">
          <Marquee list={marqueList}></Marquee>
          {!topPercent && !isMini && (
            <span className="dbo-aigc-card-percent">{percent}%</span>
          )}
        </div>
      ),
      classNames: '',
    };
  }, [percent, isMultiple]);

  if (isMini) {
    return (
      <div
        className={`dbo-aigc-card-loading-tips ${classNames}`}
        style={{ display: 'flex' }}
      >
        <Icon type="loading" style={{ marginRight: 6 }} />
        <div className="dbo-aigc-card-loading-tips-inner">
          <div className="dbo-aigc-card-loading-tips-top">{textEle}</div>
          {queenBox}
        </div>
        {loadingElement}
        {stopBtn}
      </div>
    );
  }

  return (
    // --row 按钮在右侧
    <div className={`dbo-aigc-card-loading-tips ${classNames}`}>
      <div className="dbo-aigc-card-loading-tips-inner">
        <div className="dbo-aigc-card-loading-tips-top">
          {textEle}

          {(isMultiple || topPercent) && (
            <span className="dbo-aigc-card-percent">{percent}%</span>
          )}
        </div>
        <Progress
          width={'100%'}
          theme={themeMap[status] as 'default'}
          percent={percent}
          style={{ height: 4 }}
        />
        {queenBox}
      </div>
      {loadingElement}
      {stopBtn}
    </div>
  );
}

export default function StatusTips({
  status,
  reload,
  isLittle,
  failedTipsText,
  errorType,
  isMini,
  promptInfo,
  ...props
}: LoadingTipsType & {
  errorType?: GPTErrorStatusType;
  showRefreshBtn?: boolean;
  hasTotalText?: boolean;
}) {
  return (
    <div
      className={`dbo-aigc-card-content-wrapper  ${
        ['GPT_maxLimit', 'GPT_nodata'].includes(errorType as string)
          ? 'no-regenerate'
          : ''
      }`}
    >
      <div className="dbo-aigc-card-content-inner">
        {['failed', 'cancel'].includes(status) ? (
          <FailedTips
            isLittle={isLittle}
            status={status as 'failed'}
            reload={reload}
            failedTipsText={failedTipsText}
            errorType={errorType}
            promptInfo={promptInfo}
            isMini={isMini}
            showRefreshBtn={props.showRefreshBtn}
          ></FailedTips>
        ) : (
          <LoadingTips
            status={status}
            promptInfo={promptInfo}
            isMini={isMini}
            {...props}
          ></LoadingTips>
        )}
      </div>
    </div>
  );
}
